// Vista de seguimiento de una orden de cotización (timeline + preview: bloques en solo lectura o cotización formal).

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWorkerApiWithAuth, postWorkerApiWithAuth, postWorkerApiWithAuthFormData } from '@/lib/worker-api';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import type { QuotationRequestStatus, QuotationRequestDocumentType } from '@/lib/store-types';
import type { QuotationRequestSnapshot, QuotationRequestDocumentSnapshot } from '@/hooks/use-quotation-cart';
import { toCartLine, useQuotationCart } from '@/hooks/use-quotation-cart';
import { useSourcingItems, useBrands, useOrigins, useMoqUnits, useLoadingMethods } from '@/hooks/use-store';
import { QuotationAcceptanceView } from '@/components/store/QuotationAcceptanceView';
import { QuotationBlocksView } from '@/components/store/QuotationBlocksView';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, CheckCircle2, Clock, CircleDot, Download, Upload, FileUp } from 'lucide-react';

/** All statuses in stepper order. "Enviado" y "Pendiente pago balance" unificados en un solo paso. */
const STATUS_ORDER: QuotationRequestStatus[] = [
  'draft',
  'pending',
  'en_revision',
  'cotizado',
  'pending_deposit_payment',
  'in_process',
  'shipped',
  'arriving_soon',
  'delivered',
  'finished',
];

const STATUS_LABEL: Record<QuotationRequestStatus, string> = {
  draft: 'Sin enviar',
  pending: 'Enviada',
  en_revision: 'En revisión',
  cotizado: 'Lista para aprobar',
  finished: 'Cerrada',
  pending_deposit_payment: 'Pendiente pago depósito',
  in_process: 'En proceso',
  shipped: 'En camino y Pend. pago balance',
  pending_final_payment: 'En camino y Pend. pago balance',
  arriving_soon: 'En camino',
  delivered: 'Entregado',
};

const STATUS_HELPER: Record<QuotationRequestStatus, string> = {
  draft: 'Aún estás construyendo la solicitud antes de enviarla.',
  pending: 'Solicitud enviada; nuestro equipo revisará los datos para cotizar.',
  en_revision: 'Estamos preparando tu cotización. Pronto verás los precios.',
  cotizado: 'Cotización lista. Revisa precios y acepta o rechaza.',
  finished: 'Cotización cerrada (aceptada o rechazada).',
  pending_deposit_payment: 'Esperando el pago del depósito según datos bancarios.',
  in_process: 'Orden en preparación.',
  shipped: 'Enviado; documentos de embarque disponibles. Pendiente de pago del balance.',
  pending_final_payment: 'En camino; pendiente de pago del balance.',
  arriving_soon: 'En camino; ETA puerto destino indicada.',
  delivered: 'Entregado.',
};

/** Post-acceptance statuses: misma vista (preview + documentación) para todos estos estados. */
const POST_ACCEPT_STATUSES: QuotationRequestStatus[] = [
  'pending_deposit_payment',
  'in_process',
  'shipped',
  'pending_final_payment',
  'arriving_soon',
  'delivered',
  'finished',
];

/** Document type keys (internal) → Spanish label. PDF cotización no se muestra; solo Factura proforma y comprobantes. */
const DOCUMENT_TYPE_LABEL: Record<QuotationRequestDocumentType, string> = {
  pdf_quotation: 'PDF Cotización', // oculto en UI
  pdf_proforma_invoice: 'Factura proforma',
  deposit_receipt: 'Comprobante de depósito',
  shipping_documents: 'Documentos de embarque',
  balance_payment_receipt: 'Comprobante de pago balance',
};

/** Tipos de documento que se muestran en la lista (sin PDF Cotización; admin genera/sube el resto). */
const DISPLAY_DOCUMENT_TYPES: QuotationRequestDocumentType[] = [
  'pdf_proforma_invoice',
  'deposit_receipt',
  'shipping_documents',
  'balance_payment_receipt',
];

/** Solo el admin genera PDFs; el cliente nunca. */
const PDF_GENERATABLE_TYPES: QuotationRequestDocumentType[] = [];

/** Cliente solo sube comprobante de depósito y comprobante de pago balance (cuando toca por estado). */
function canClientUploadDocType(status: QuotationRequestStatus, documentType: QuotationRequestDocumentType): boolean {
  switch (documentType) {
    case 'deposit_receipt':
      return status === 'pending_deposit_payment';
    case 'balance_payment_receipt':
      return status === 'shipped' || status === 'pending_final_payment';
    case 'pdf_quotation':
    case 'pdf_proforma_invoice':
    case 'shipping_documents':
      return false;
    default:
      return false;
  }
}

/** Mostrar fila de este tipo de documento al cliente según estado (misma vista para todos los post-aceptación, incluido finished). */
function shouldShowDocumentTypeForClient(status: QuotationRequestStatus, documentType: QuotationRequestDocumentType): boolean {
  const postAccept = ['pending_deposit_payment', 'in_process', 'shipped', 'pending_final_payment', 'arriving_soon', 'delivered', 'finished'];
  switch (documentType) {
    case 'pdf_proforma_invoice':
      return postAccept.includes(status);
    case 'deposit_receipt':
      return postAccept.includes(status);
    case 'shipping_documents':
      return ['shipped', 'pending_final_payment', 'arriving_soon', 'delivered', 'finished'].includes(status);
    case 'balance_payment_receipt':
      return ['shipped', 'pending_final_payment', 'arriving_soon', 'delivered', 'finished'].includes(status);
    case 'pdf_quotation':
      return false;
    default:
      return true;
  }
}

function orderNumber(id: string): string {
  return id.slice(-8).toUpperCase();
}

export default function StoreQuotationOrder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [request, setRequest] = useState<QuotationRequestSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [respondPending, setRespondPending] = useState(false);
  const [duplicatePending, setDuplicatePending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { setCurrentRequestId } = useQuotationCart();

  const { data: allSourcingItems } = useSourcingItems(null);
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: moqUnits } = useMoqUnits();
  const { data: loadingMethods } = useLoadingMethods();

  /** Líneas de la orden para QuotationBlocksView / QuotationAcceptanceView (mismo formato que admin). */
  const cartLines = useMemo(() => {
    if (!request?.items?.length) return [];
    return request.items.map((it) =>
      toCartLine(
        { ...it, moq_value: (it as { moq_value?: number }).moq_value },
        allSourcingItems ?? [],
        brands ?? [],
        origins ?? [],
        moqUnits ?? []
      )
    );
  }, [request?.items, allSourcingItems, brands, origins, moqUnits]);

  const fetchRequest = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!id) return;
      const silent = options?.silent === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      } else {
        setIsRefreshing(true);
      }
      let cancelled = false;
      try {
        const res = await getWorkerApiWithAuth<QuotationRequestSnapshot>(
          `/api/quotation-request?id=${encodeURIComponent(id)}`,
          () => supabase.auth.getSession()
        );
        if (cancelled) return;
        if (!res.ok) {
          if (!silent) {
            setError(res.error || 'No se ha podido cargar la orden de cotización.');
          }
        } else {
          setRequest(res.data);
          setError(null);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setIsRefreshing(false);
        }
      }
      return () => {
        cancelled = true;
      };
    },
    [id]
  );

  useEffect(() => {
    fetchRequest({ silent: false });
  }, [fetchRequest]);

  /** Refetch: recarga la orden desde el API (actualiza estado tras cambio de admin u otro dispositivo). Retorna la promesa para poder await. */
  const refetchRequest = useCallback(() => {
    return fetchRequest({ silent: true });
  }, [fetchRequest]);

  // Actualización en tiempo real: cuando admin o cliente cambian el estado, refetch para mantener la vista sincronizada.
  // Requiere que quotation_requests esté en la publicación supabase_realtime (Database → Replication).
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`quotation_request:${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'quotation_requests', filter: `id=eq.${id}` },
        refetchRequest
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          // Realtime no disponible o no configurado: el polling por visibilidad cubre el caso.
        }
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, refetchRequest]);

  // Al volver a la pestaña, refetch por si el admin cambió el estado (útil si Realtime no llega o el usuario estaba en otra pestaña).
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && id) refetchRequest();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [id, refetchRequest]);

  // Polling de respaldo mientras la orden está pendiente de cotizar: el cliente espera que admin pase a "cotizado".
  useEffect(() => {
    if (!id || !request) return;
    const status = request.status ?? 'draft';
    if (status !== 'pending' && status !== 'en_revision') return;
    const interval = setInterval(refetchRequest, 15000); // cada 15 s
    return () => clearInterval(interval);
  }, [id, request?.status, refetchRequest]);

  const handleAccept = useCallback(async () => {
    if (!request?.id) return;
    setRespondPending(true);
    const res = await postWorkerApiWithAuth(
      '/api/quotation-request/respond',
      { request_id: request.id, accepted: true },
      () => supabase.auth.getSession()
    );
    if (res.ok) {
      await refetchRequest();
      toast({ title: 'Cotización aceptada' });
    } else {
      toast({ title: res.error ?? 'Error al aceptar', variant: 'destructive' });
    }
    setRespondPending(false);
  }, [request?.id, toast, refetchRequest]);

  const handleRequestChange = useCallback(
    async (comment: string) => {
      if (!request?.id) return;
      setRespondPending(true);
      const res = await postWorkerApiWithAuth(
        '/api/quotation-request/respond',
        { request_id: request.id, request_change_comment: comment },
        () => supabase.auth.getSession()
      );
      setRespondPending(false);
      if (res.ok) {
        refetchRequest();
        toast({ title: 'Solicitud de cambios enviada. La cotización volverá a revisión.' });
      } else {
        toast({ title: res.error ?? 'Error al solicitar cambios', variant: 'destructive' });
      }
    },
    [request?.id, toast, refetchRequest]
  );

  const status = (request?.status ?? 'draft') as QuotationRequestStatus;
  const statusForStepper = status === 'pending_final_payment' ? 'shipped' : status;
  const statusIndex = STATUS_ORDER.indexOf(statusForStepper);
  const currentIndex = statusIndex >= 0 ? statusIndex : Math.max(0, STATUS_ORDER.indexOf('finished'));
  const isPostAccept = POST_ACCEPT_STATUSES.includes(status);
  const requestId = request?.id ?? '';
  const quotationPdfExists = (request?.documents ?? []).some(
    (d) => d.document_type === 'pdf_quotation' && !!d.file_url
  );
  const proformaPdfExists = (request?.documents ?? []).some(
    (d) => d.document_type === 'pdf_proforma_invoice' && !!d.file_url
  );

  const [docUploadPending, setDocUploadPending] = useState<string | null>(null);
  const [docGeneratePending, setDocGeneratePending] = useState<string | null>(null);
  const [pdfQuotationDownloadPending, setPdfQuotationDownloadPending] = useState(false);
  const [proformaDownloadPending, setProformaDownloadPending] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const getSession = useCallback(() => supabase.auth.getSession(), []);

  // Mientras la solicitud está en cotizado y el PDF de cotización aún no existe,
  // mostrar el botón como "generando" (spinner + disabled).
  useEffect(() => {
    if (status === 'cotizado' && !quotationPdfExists) {
      setPdfQuotationDownloadPending(true);
    } else {
      setPdfQuotationDownloadPending(false);
    }
  }, [status, quotationPdfExists]);

  // Polling específico para la generación de PDF: cuando el admin marca como cotizado
  // se genera en background; aquí recargamos periódicamente hasta que aparezca el PDF.
  useEffect(() => {
    if (!id || !request) return;
    if (status !== 'cotizado') return;
    if (quotationPdfExists) return;
    const interval = setInterval(refetchRequest, 10000);
    return () => clearInterval(interval);
  }, [id, request, status, quotationPdfExists, refetchRequest]);

  // Proforma: mientras esté pendiente de pago y aún no exista el PDF (se genera en background al aceptar), mostrar loading en el botón.
  useEffect(() => {
    const pendingPayment = status === 'pending_deposit_payment' || status === 'pending_final_payment' || status === 'shipped';
    if (pendingPayment && !proformaPdfExists) {
      setProformaDownloadPending(true);
    } else {
      setProformaDownloadPending(false);
    }
  }, [status, proformaPdfExists]);

  // Polling para proforma: al aceptar se genera en background; refetch hasta que aparezca.
  useEffect(() => {
    if (!id || !request) return;
    const pendingPayment = status === 'pending_deposit_payment' || status === 'pending_final_payment' || status === 'shipped';
    if (!pendingPayment || proformaPdfExists) return;
    const interval = setInterval(refetchRequest, 10000);
    return () => clearInterval(interval);
  }, [id, request, status, proformaPdfExists, refetchRequest]);

  /** Descargar PDF cotización: si ya existe, abrir; si no, generarlo por API (fallback manual cuando no se generó en background). */
  const handleDownloadQuotationPdf = useCallback(async (): Promise<string | null> => {
    if (!requestId) return null;
    const docs = request?.documents ?? [];
    const latestQuotation = docs
      .filter((d) => d.document_type === 'pdf_quotation')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    if (latestQuotation?.file_url) {
      return latestQuotation.file_url;
    }
    setPdfQuotationDownloadPending(true);
    const res = await postWorkerApiWithAuth<{ pdf_url?: string }>(
      '/api/generate-request-pdf',
      { request_id: requestId, variant: 'quotation' },
      getSession
    );
    setPdfQuotationDownloadPending(false);
    if (res.ok && res.data?.pdf_url) {
      refetchRequest();
      return res.data.pdf_url;
    }
    toast({
      title: res.error ?? 'Error al generar el PDF de cotización',
      variant: 'destructive',
    });
    return null;
  }, [requestId, request?.documents, getSession, refetchRequest, toast]);

  /** Descargar factura proforma y datos bancarios: abre el PDF de proforma (generado al aceptar) o lo pide al API. */
  const handleDownloadProformaPdf = useCallback(async (): Promise<string | null> => {
    if (!requestId) return null;
    const docs = request?.documents ?? [];
    const latestProforma = docs
      .filter((d) => d.document_type === 'pdf_proforma_invoice')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    if (latestProforma?.file_url) {
      return latestProforma.file_url;
    }
    setProformaDownloadPending(true);
    const res = await postWorkerApiWithAuth<{ pdf_url?: string }>(
      '/api/generate-request-pdf',
      { request_id: requestId, variant: 'proforma_invoice' },
      getSession
    );
    setProformaDownloadPending(false);
    if (res.ok && res.data?.pdf_url) {
      refetchRequest();
      return res.data.pdf_url;
    }
    toast({
      title: res.error ?? 'Error al generar la factura proforma',
      variant: 'destructive',
    });
    return null;
  }, [requestId, request?.documents, getSession, refetchRequest, toast]);

  const handleUploadDocument = useCallback(
    async (documentType: QuotationRequestDocumentType, file: File) => {
      if (!requestId) return;
      setDocUploadPending(documentType);
      const formData = new FormData();
      formData.set('request_id', requestId);
      formData.set('document_type', documentType);
      formData.append('file', file);
      const res = await postWorkerApiWithAuthFormData<{ file_url?: string; document_id?: string }>(
        '/api/quotation-request/upload-document',
        formData,
        getSession
      );
      setDocUploadPending(null);
      if (res.ok) {
        refetchRequest();
        toast({ title: 'Documento subido correctamente.' });
      } else {
        toast({ title: res.error ?? 'Error al subir', variant: 'destructive' });
      }
    },
    [requestId, getSession, refetchRequest, toast]
  );

  const handleDuplicate = useCallback(async () => {
    if (!requestId) return;
    setDuplicatePending(true);
    try {
      const res = await postWorkerApiWithAuth<{ request_id?: string }>(
        '/api/quotation-request/duplicate',
        { source_request_id: requestId },
        getSession
      );
      if (res.ok && res.data?.request_id) {
        setCurrentRequestId(res.data.request_id);
        toast({ title: 'Solicitud duplicada', description: 'Se ha creado una copia con el mismo estado.' });
        navigate(`/store/quotations/${res.data.request_id}`);
      } else {
        toast({ title: res.error ?? 'Error al duplicar', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error al duplicar la solicitud', variant: 'destructive' });
    } finally {
      setDuplicatePending(false);
    }
  }, [requestId, getSession, setCurrentRequestId, navigate, toast]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" className="mb-1 -ml-2" onClick={() => navigate('/store/quotations')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver al historial
          </Button>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
            Orden de cotización {request ? `#${orderNumber(request.id)}` : ''}
          </h1>
          {request && (
            <p className="text-sm text-zinc-600 mt-1">
              Esta orden requiere tu revisión y aprobación cuando la cotización esté lista.
            </p>
          )}
        </div>
        {request && (
          <div className="flex flex-col items-end gap-1 text-right">
            <Badge variant="outline" className="text-xs">
              Estado actual: {STATUS_LABEL[status]}
            </Badge>
            <span className="text-[11px] text-zinc-500">
              Creada el{' '}
              {new Date(('created_at' in request ? (request as { created_at?: string | null }).created_at : '') ?? '').toLocaleDateString('es-ES', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
        </div>
      )}

      {error && !loading && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-6">
            <p className="text-sm text-amber-900">{error}</p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && request && (
        <div className="flex flex-col gap-8">
          {/* Línea 1: seguimiento como línea horizontal (stepper) */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Seguimiento de la orden</CardTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                Recorrido desde el envío hasta el cierre. Cuando esté cotizada, podrás aceptarla, pedir cambios o descargar en PDF.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="relative flex items-start">
                {/* Línea horizontal que une los pasos */}
                <div
                  className="absolute left-0 right-0 top-[11px] h-0.5 bg-zinc-200"
                  aria-hidden
                />
                {STATUS_ORDER.map((st, idx) => {
                  const reached = idx <= currentIndex;
                  const isCurrent = idx === currentIndex;
                  const Icon = isCurrent ? CircleDot : reached ? CheckCircle2 : Clock;
                  const showEtd = st === 'shipped' && isCurrent && request?.etd_origin_port;
                  const showEta = (st === 'arriving_soon' || st === 'delivered') && isCurrent && request?.eta_destination_port;
                  return (
                    <div
                      key={st}
                      className="relative flex flex-1 flex-col items-center min-w-0"
                      title={STATUS_HELPER[st]}
                    >
                      <span
                        className={[
                          'z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 bg-white',
                          isCurrent
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : reached
                              ? 'border-emerald-500 text-emerald-600'
                              : 'border-zinc-300 bg-white text-zinc-400',
                        ].join(' ')}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span
                        className={[
                          'mt-1.5 text-center text-[11px] font-medium leading-tight',
                          isCurrent ? 'text-emerald-700' : reached ? 'text-zinc-700' : 'text-zinc-400',
                        ].join(' ')}
                      >
                        {STATUS_LABEL[st]}
                        {isCurrent && ' (actual)'}
                      </span>
                      {showEtd && (
                        <span className="mt-0.5 text-[10px] text-zinc-500">
                          ETD: {new Date(request.etd_origin_port!).toLocaleDateString('es-ES', { dateStyle: 'short' })}
                        </span>
                      )}
                      {showEta && (
                        <span className="mt-0.5 text-[10px] text-zinc-500">
                          ETA: {new Date(request.eta_destination_port!).toLocaleDateString('es-ES', { dateStyle: 'short' })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Línea 2: preview de la orden (solo lectura) o cotización formal; datos bancarios van dentro del preview, debajo de cabecera y botones */}
          <div className="space-y-3">
            {(status === 'draft' || status === 'pending' || status === 'en_revision') && (
              <Card className="border-zinc-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Contenido de la orden</CardTitle>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Vista previa de tu solicitud. Los ítems no son editables aquí; cuando la cotización esté lista podrás aprobarla o rechazarla debajo.
                  </p>
                </CardHeader>
                <CardContent className="min-w-0 overflow-x-auto">
                  <QuotationBlocksView
                    request={request}
                    items={cartLines}
                    mode="admin-view"
                    readOnly={true}
                    adminProps={{ allSourcingItems: allSourcingItems ?? [], loadingMethods: loadingMethods ?? [] }}
                  />
                </CardContent>
              </Card>
            )}

            {(status === 'cotizado' || isPostAccept) && (
              <QuotationAcceptanceView
                requestId={request.id}
                blocks={request.blocks}
                items={cartLines}
                status={status}
                lead={request.lead ?? undefined}
                incoterm={request.incoterm}
                destinationPortOrAddress={request.destination_port_or_address}
                addressee_company={request.addressee_company}
                addressee_contact={request.addressee_contact}
                final_price_text={request.final_price_text}
                payment_type={request.payment_type}
                payment_method={request.payment_method}
                observaciones={request.observaciones}
                documentTitle={request.document_title}
                headerImageUrl={request.header_image_url}
                sealImageUrl={request.seal_image_url}
                finalMessage={request.final_message}
                showFinalMessage={request.show_final_message}
                quotationCode={orderNumber(request.id)}
                showActions={status === 'cotizado'}
                responded={status === 'finished' || isPostAccept}
                outcome={request.outcome ?? undefined}
                onAccept={status === 'cotizado' ? handleAccept : undefined}
                onRequestChange={status === 'cotizado' ? handleRequestChange : undefined}
                respondPending={respondPending}
                allSourcingItems={allSourcingItems ?? []}
                loadingMethods={loadingMethods ?? []}
                onDownloadQuotationPdf={status === 'cotizado' ? handleDownloadQuotationPdf : undefined}
                pdfDownloadPending={pdfQuotationDownloadPending}
                onDuplicate={handleDuplicate}
                duplicatePending={duplicatePending}
                primaryAction={
                  status === 'pending_deposit_payment' ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-2 border-primary text-primary hover:bg-primary/10"
                        disabled={proformaDownloadPending}
                        onClick={async () => {
                          const url = await handleDownloadProformaPdf();
                          if (url) window.open(url, '_blank');
                        }}
                      >
                        {proformaDownloadPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        {proformaDownloadPending ? 'Generando factura…' : 'Descargar factura y datos de bancarios'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => fileInputRefs.current['deposit_receipt']?.click()}
                        disabled={!!docUploadPending}
                        className="gap-2 btn-accept-highlight"
                      >
                        {docUploadPending === 'deposit_receipt' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        Subir comprobante de depósito
                      </Button>
                    </>
                  ) : status === 'shipped' || status === 'pending_final_payment' ? (
                    (() => {
                      const shippingDocs = (request?.documents ?? []).filter((d) => d.document_type === 'shipping_documents');
                      const latestShipping = shippingDocs.length > 0
                        ? shippingDocs.reduce((a, b) => (new Date(b.created_at).getTime() > new Date(a.created_at).getTime() ? b : a))
                        : null;
                      return (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            className="gap-2 border-primary text-primary hover:bg-primary/10"
                            disabled={proformaDownloadPending}
                            onClick={async () => {
                              const url = await handleDownloadProformaPdf();
                              if (url) window.open(url, '_blank');
                            }}
                          >
                            {proformaDownloadPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            {proformaDownloadPending ? 'Generando factura…' : 'Descargar factura y datos bancarios'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="gap-2 border-primary text-primary hover:bg-primary/10"
                            disabled={!latestShipping?.file_url}
                            onClick={() => {
                              if (latestShipping?.file_url) window.open(latestShipping.file_url, '_blank');
                            }}
                          >
                            <Download className="h-4 w-4" />
                            {latestShipping?.file_url ? 'Descargar documentos de embarque' : 'Documentos (pendientes)'}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => fileInputRefs.current['balance_payment_receipt']?.click()}
                            disabled={!!docUploadPending}
                            className="gap-2 btn-accept-highlight"
                          >
                            {docUploadPending === 'balance_payment_receipt' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                            Subir comprobante de pago balance
                          </Button>
                        </>
                      );
                    })()
                  ) : undefined
                }
                bankDetailsSection={
                  isPostAccept && request?.bank_account && (status === 'pending_deposit_payment' || status === 'pending_final_payment' || status === 'shipped')
                    ? (() => {
                        const ba = request.bank_account;
                        return (
                          <div className="space-y-3">
                            <h2 className="text-base font-bold text-zinc-800">Nuestros datos bancarios</h2>
                            <p className="text-xs text-zinc-500 -mt-1">Realiza la transferencia a la siguiente cuenta.</p>
                            <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-3 gap-y-1.5 text-sm">
                              {ba.label && (<><dt className="text-zinc-500 font-medium">Cuenta</dt><dd>{ba.label}</dd></>)}
                              {ba.bank_name && (<><dt className="text-zinc-500 font-medium">Banco</dt><dd>{ba.bank_name}</dd></>)}
                              {ba.beneficiary_bank_address && (<><dt className="text-zinc-500 font-medium">Dirección del banco beneficiario</dt><dd>{ba.beneficiary_bank_address}</dd></>)}
                              {ba.account_holder && (<><dt className="text-zinc-500 font-medium">Titular</dt><dd>{ba.account_holder}</dd></>)}
                              {ba.account_number && (<><dt className="text-zinc-500 font-medium">Número de cuenta</dt><dd>{ba.account_number}</dd></>)}
                              {ba.iban && (<><dt className="text-zinc-500 font-medium">IBAN</dt><dd className="font-mono text-xs">{ba.iban}</dd></>)}
                              {ba.swift && (<><dt className="text-zinc-500 font-medium">SWIFT</dt><dd className="font-mono text-xs">{ba.swift}</dd></>)}
                              {ba.currency && (<><dt className="text-zinc-500 font-medium">Moneda</dt><dd>{ba.currency}</dd></>)}
                            </dl>
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                              <p className="text-xs font-semibold text-amber-800 mb-1">Concepto de transferencia (obligatorio)</p>
                              <p className="text-sm text-amber-900 leading-snug">En el concepto de la transferencia <strong>debe indicar el número de solicitud</strong>:</p>
                              <p className="mt-2 font-mono text-base font-bold text-amber-900 tracking-wide" aria-label="Referencia para concepto de transferencia">#{orderNumber(request.id)}</p>
                            </div>
                            {(ba.intermediary_bank_name || ba.intermediary_bank_swift) && (
                              <div className="rounded-md bg-zinc-100 border border-zinc-200 p-3">
                                <p className="text-xs font-semibold text-zinc-700 mb-1.5">Banco intermediario / corresponsal</p>
                                <p className="text-xs text-zinc-600 mb-2">Solo necesario si el banco del cliente lo requiere.</p>
                                <dl className="grid grid-cols-[minmax(0,5rem)_1fr] gap-x-2 gap-y-1 text-sm">
                                  {ba.intermediary_bank_name && (<><dt className="text-zinc-500 font-medium">Banco</dt><dd>{ba.intermediary_bank_name}</dd></>)}
                                  {ba.intermediary_bank_address && (<><dt className="text-zinc-500 font-medium">Dirección</dt><dd>{ba.intermediary_bank_address}</dd></>)}
                                  {ba.intermediary_bank_swift && (<><dt className="text-zinc-500 font-medium">SWIFT</dt><dd className="font-mono text-xs">{ba.intermediary_bank_swift}</dd></>)}
                                </dl>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    : undefined
                }
                documentationSection={isPostAccept ? (
                  <div className="space-y-4">
                    <h2 className="text-sm font-bold text-zinc-700">Documentación</h2>
                    <p className="text-xs text-zinc-500 -mt-2">
                      Descarga los documentos existentes o sube comprobantes cuando corresponda.
                    </p>
                    {DISPLAY_DOCUMENT_TYPES.filter((docType) => shouldShowDocumentTypeForClient(status, docType)).map((docType) => {
                      const docsOfType = (request?.documents ?? []).filter((d) => d.document_type === docType);
                      const latest = docsOfType.length > 0
                        ? docsOfType.reduce((a, b) => (new Date(b.created_at).getTime() > new Date(a.created_at).getTime() ? b : a))
                        : null;
                      const label = DOCUMENT_TYPE_LABEL[docType];
                      const canUpload = canClientUploadDocType(status, docType);
                      const uploadPending = docUploadPending === docType;
                      return (
                        <div key={docType} className="flex flex-wrap items-center justify-between gap-2 rounded border border-zinc-100 bg-zinc-50/50 p-3">
                          <div className="min-w-0">
                            <p className="font-medium text-zinc-800 text-sm">{label}</p>
                            {latest ? (
                              <p className="text-xs text-zinc-500 mt-0.5">
                                {latest.file_name || 'Documento'} · {new Date(latest.created_at).toLocaleDateString('es-ES', { dateStyle: 'short' })}
                              </p>
                            ) : (
                              <p className="text-xs text-zinc-500 mt-0.5">Sin documento</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {docType === 'pdf_proforma_invoice' && proformaDownloadPending ? (
                              <Button variant="outline" size="sm" disabled className="gap-1">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Generando…
                              </Button>
                            ) : latest ? (
                              <Button variant="outline" size="sm" asChild className="gap-1">
                                <a href={latest.file_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="h-3.5 w-3.5" />
                                  Descargar
                                </a>
                              </Button>
                            ) : null}
                            {/* Solo el admin sube Factura proforma; el cliente solo sube comprobantes de depósito y balance. Ocultar Subir cuando no aplica. */}
                            {canUpload && (
                              <>
                                <input
                                  type="file"
                                  ref={(el) => { fileInputRefs.current[docType] = el; }}
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleUploadDocument(docType, file);
                                    e.target.value = '';
                                  }}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => fileInputRefs.current[docType]?.click()}
                                  disabled={!!uploadPending}
                                  className="gap-1"
                                >
                                  {uploadPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                  Subir
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : undefined}
              />
            )}

            <p className="text-xs text-zinc-500 text-right">
              ¿Dudas con esta orden? Contacta con el equipo Padmora indicando el número de orden.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

